import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "/logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Product', path: '/product' },
  { name: 'Solutions', path: '/solutions' },
  { name: 'Modules', path: '/modules' },
  { name: 'Standards', path: '/standards' },
  { name: 'Security', path: '/security' },
  { name: 'About', path: '/about' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav 
      data-testid="navbar"
      className={`nav-sticky glass-nav ${scrolled ? 'shadow-sm' : ''}`}
      style={{ transition: 'box-shadow 0.3s ease' }}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="Certisfy - A subsidiary of AGRIN" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  location.pathname === link.path
                    ? 'text-[#1F7A3A] bg-[#F0FDF4]'
                    : 'text-slate-600 hover:text-[#1F7A3A] hover:bg-slate-50'
                }`}
                style={{ transition: 'background-color 0.2s, color 0.2s' }}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact?demo=true" data-testid="nav-request-demo">
              <Button className="bg-[#1F7A3A] text-white hover:bg-[#145A2A] rounded-full px-6 py-2.5 font-semibold shadow-md hover:shadow-lg">
                Request a Demo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            data-testid="mobile-menu"
            className="lg:hidden py-4 border-t border-slate-200 mobile-menu-enter"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`px-4 py-3 rounded-lg text-sm font-medium ${
                    location.pathname === link.path
                      ? 'text-[#1F7A3A] bg-[#F0FDF4]'
                      : 'text-slate-600 hover:text-[#1F7A3A] hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-slate-200">
                <Link to="/contact" className="w-full">
                  <Button variant="outline" className="w-full border-slate-300">
                    Talk to Us
                  </Button>
                </Link>
                <Link to="/contact?demo=true" className="w-full">
                  <Button className="bg-[#1F7A3A] text-white hover:bg-[#145A2A] w-full rounded-full py-2.5 font-semibold">
                    Request a Demo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
